import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {StompConnectionStatus} from '../ws/stomp-connection-status.enum';
import {StompSubscription} from '../ws/stomp-subscription';
import {StompFrame} from '../ws/stomp-frame';
import {Directive, OnDestroy} from '@angular/core';
import {StompClose, StompEvent, StompEventType} from './stomp-event';
import {fromWorker} from 'observable-webworker';
import {StompWorker} from './stomp.worker';
import {finalize, take, tap} from 'rxjs/operators';
import {trimNull} from '../helpers/kluh';
import {applyReconnectJitter, nextReconnectDelay} from './stomp-backoff';
import {StompWorkerWatchdog, WORKER_SILENCE_TIMEOUT_MS} from './stomp-worker-watchdog';
import {readStompWorkerEnvironment, shouldUseDedicatedWorker} from './stomp-worker-strategy';

@Directive()
export abstract class StompService implements OnDestroy {
    protected _connectionStatus$: BehaviorSubject<StompConnectionStatus> = new BehaviorSubject<StompConnectionStatus>(StompConnectionStatus.DISCONNECTED);
    protected _username$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private stompSubscriptions: StompSubscription<any>[] = [];
    private isConnected: boolean;
    private isConnecting: boolean;
    private stompSubject: Subject<StompEvent>;
    private heartbeatInterval = 5000;
    private connectionTimeout: number | null = null;
    private connectionDelay = 0;
    private connectionTry = 0;
    private ob: Observable<StompEvent> | null = null;
    private workerSubscription: Subscription | null = null;
    // R2CLOUD-1165: o worker do STOMP às vezes não inicia. Ver `stomp-worker-watchdog.ts`.
    private readonly workerWatchdog = new StompWorkerWatchdog();
    // R2CLOUD-1169: uma vez que o Web Worker dedicado falhe NESTA sessão da página, não se tenta de
    // novo — ver `markDedicatedWorkerUnavailable()`.
    private dedicatedWorkerUnavailable = false;

    protected constructor() {
        this.isConnected = false;
        this.isConnecting = false;
        this.stompSubject = new Subject<StompEvent>();
    }

    private initWorker(): void {
        if (this.ob) {
            return;
        }
        const input$ = this.stompSubject.asObservable()
            .pipe(tap((stompEvent) => {
                console.debug('sending event');
                console.debug(stompEvent);
            }));
        // R2CLOUD-1169: a escolha não é mais só "o browser tem `Worker`?". Com o ngsw controlando a
        // página, o primeiro Web Worker dedicado NÃO INICIA (8 de 8 cargas medidas em DEV), e em PROD
        // sempre há service worker. A regra e as medições estão em `stomp-worker-strategy.ts`.
        if (!this.dedicatedWorkerUnavailable && shouldUseDedicatedWorker(readStompWorkerEnvironment())) {
            console.debug('using worker');
            this.ob = fromWorker<StompEvent, StompEvent>(() => new Worker(new URL('./stomp.worker', import.meta.url), {type: 'module'}), input$);
        } else {
            console.debug('stomp no main thread (sem Web Worker dedicado)');
            this.ob = new StompWorker().work(input$);
        }
        this.workerSubscription = this.ob.subscribe((stompEvent) => {
            this.onStompEvent(stompEvent);
        }, (error: unknown) => {
            // R2CLOUD-1169: este ramo engolia o erro sem UMA linha de log, e por isso o diagnóstico
            // do R2CLOUD-1165 registrou "nenhum evento `error`" como se fosse fato medido — não
            // era, era o silêncio daqui. O `fromWorker` encaminha para cá o `worker.onerror`, que é
            // como um script de worker que não carrega (404 depois de um deploy, erro de parse) se
            // manifesta.
            //
            // Limpar o estado importa tanto quanto logar: quando um Observable ERRA, o `complete`
            // não roda, então `this.ob` continuava preenchido e o `initWorker()` seguinte caía no
            // `if (this.ob) return` e reusava um observable morto — sessão STOMP nunca mais, sem
            // nada no console. O watchdog só cobre a janela em que está armado.
            console.error('stomp worker falhou — descartando para permitir nova tentativa', error);
            this.isConnecting = false;
            this.workerSubscription = null;
            this.ob = null;
            // R2CLOUD-1169: a retentativa vai pelo main thread. Se o erro veio do Web Worker
            // dedicado, repetir o mesmo caminho só repetiria a falha; se já estávamos no main
            // thread, marcar é inofensivo (o caminho escolhido não muda).
            this.markDedicatedWorkerUnavailable();
            if (this.stompSubscriptions.length > 0) {
                this.connect();
            }
        }, () => {
            console.debug('worker complete');
            this.workerSubscription = null;
            this.ob = null;
        });
    }

    /**
     * Descarta o worker atual (R2CLOUD-1165).
     *
     * O `unsubscribe` dispara o teardown do `fromWorker`, que chama `worker.terminate()`
     * (`terminateOnComplete: true`, o default da lib). **Terminar ANTES de criar o próximo é o
     * conserto, não um detalhe de limpeza**: o worker que não iniciou não está morto, está atrasado
     * — um deles acordou ~71 s depois e abriu o socket —, e sem o `terminate()` ele acordaria com o
     * `OPEN` ainda na fila e abriria uma SEGUNDA sessão STOMP para o mesmo usuário.
     *
     * `this.ob = null` é o que faz o `initWorker()` seguinte criar OUTRO worker em vez de reusar o
     * observable antigo. Medido: worker novo na mesma aba sobe 3 de 3 enquanto o antigo segue parado.
     */
    private discardWorker(): void {
        this.workerSubscription?.unsubscribe();
        this.workerSubscription = null;
        this.ob = null;
    }

    /**
     * Desqualifica o Web Worker dedicado pelo resto desta sessão da página (R2CLOUD-1169).
     *
     * A marca vale para a instância viva do service, e não é persistida: cada carga do portal volta a
     * consultar `shouldUseDedicatedWorker()`. Não persistir é deliberado — o defeito depende do
     * estado do ngsw na carga, e um flag guardado em storage esconderia a volta do caminho saudável.
     *
     * Marcada, o `initWorker()` seguinte usa `new StompWorker().work(input$)` (main thread), que é o
     * caminho medido em 0 de 3 cargas mudas com o service worker ativo.
     */
    private markDedicatedWorkerUnavailable(): void {
        this.dedicatedWorkerUnavailable = true;
    }

    /**
     * O worker não deu sinal de vida dentro do prazo: a tentativa de conexão morreu (R2CLOUD-1165).
     *
     * Sem isto, `isConnecting` continuava `true` para sempre — só `onStompConnect()` e `onClose()` o
     * zeram, e os dois dependem de mensagem VINDA do worker. Cada `stompTopic()` seguinte apenas
     * inflava o backoff (medido: 1000 → 60000 em sete chamadas, ZERO retentativas), e a única saída
     * do produto era o `restartApps()` → `location.reload()`, ~118 s depois e só se o usuário
     * clicasse.
     *
     * R2CLOUD-1169: a retentativa agora TROCA de caminho em vez de repetir o que acabou de falhar.
     * Antes, o `connect()` daqui criava OUTRO Web Worker dedicado sob a mesma página controlada pelo
     * ngsw — o segundo respondia, mas só em ~6,7 s, e a sessão STOMP só subia aos 21-26 s. Indo para
     * o main thread, o socket abre em 728-767 ms.
     */
    private onWorkerSilent(): void {
        console.warn(`stomp worker mudo por ${WORKER_SILENCE_TIMEOUT_MS}ms — descartando o worker e reconectando pelo main thread (R2CLOUD-1169)`);
        this.isConnecting = false;
        this.discardWorker();
        // Marcar ANTES do `connect()`: é o que faz o `initWorker()` da retentativa ir pelo main
        // thread em vez de criar outro Web Worker dedicado sob a mesma página controlada pelo ngsw.
        this.markDedicatedWorkerUnavailable();
        // Sem assinatura viva não há para quem reconectar: quem zerou a lista já chamou `disconnect()`.
        if (this.stompSubscriptions.length > 0) {
            this.connect();
        }
    }

    protected abstract url(): Observable<string>;

    get connectionStatus$(): Observable<StompConnectionStatus> {
        return this._connectionStatus$.asObservable();
    }

    get username$(): Observable<string | null> {
        return this._username$;
    }

    stompSubscribe<T>(destination: string): Observable<T> {
        return this.stompTopic(destination);
    }

    publish<T>(destination: string, object: T): void {
        this.send(destination, object);
    }

    stompTopic<T>(destination: string): Observable<T> {
        console.debug(`stomp topic: ${destination}`);
        let stompSubscription = this.stompSubscriptions.find((o) => o.destination === destination);
        if (!stompSubscription) {
            console.debug(`creating new stomp subscription for ${destination}, isConnected ${this.isConnected}`);
            stompSubscription = new StompSubscription<T>(destination, (o => {
                this.sendUnsubscribe(o);
                const index = this.stompSubscriptions.findIndex((item) => item.id === o.id);
                if (index > -1) {
                    this.stompSubscriptions.splice(index, 1);
                }
                console.debug('subscriptions length: ' + this.stompSubscriptions.length);
                if (this.stompSubscriptions.length === 0) {
                    this.disconnect();
                }
            }));
            this.stompSubscriptions.push(stompSubscription);
            if (this.isConnected) {
                this.sendSubscribe(stompSubscription);
            } else {
                this.connect();
            }
        }
        return stompSubscription.observable;
    }

    send<T>(destination: string, object: T): void {
        if (this.isConnected) {
            const message = JSON.stringify(object);
            this.stompSubject.next({
                type: StompEventType.FRAME,
                frame: {command: 'SEND', headers: {destination: destination}, body: message}
            });
        }
    }

    protected connect(): void {
        if (this.connectionTimeout !== null) {
            // já existe uma reconexão agendada — não duplicar o timer nem inflar o backoff
            console.debug('stomp connect skipped, reconnect already scheduled');
            return;
        }
        if (!this.isConnected && !this.isConnecting) {
            // R2CLOUD-1165: o backoff cresce por TENTATIVA, não por chamada. Esta linha vivia fora
            // da guarda e inflava mesmo quando `connect()` não agendava nada — que é o estado de
            // toda chamada feita enquanto `isConnecting` está `true`. Um load de `/desktop` assina
            // ~10 destinos, então o delay chegava a 60000 **sem uma única retentativa** (medido:
            // `1000 → 2000 → 4000 → 8000 → 16000 → 32000 → 60000`, sete chamadas, zero tentativas).
            // Com o watchdog, isso deixou de ser cosmético: a reconexão dele era agendada com o
            // backoff já estourado e demorava até 90 s (medido em DEV: um login em falha só abriu o
            // socket ~3 min depois). A guarda de `connectionTimeout` acima já pulava a inflação pelo
            // mesmo motivo — agora as duas concordam. O caso do R2CLOUD-1098 (queda em massa pelo
            // reload do ingress) continua inflando: ali `onClose()` chega com as duas flags em
            // `false` e a tentativa é real.
            if (this.connectionTry > 0) {
                this.connectionDelay = nextReconnectDelay(this.connectionDelay);
            }
            this.connectionTimeout = window.setTimeout(() => {
                console.debug('get url');
                // R2CLOUD-1128: `connectionTimeout` NÃO pode ser zerado aqui. O `url()` faz um
                // POST /firebase/login antes do handshake (R2CLOUD-1099), e durante esse round-trip
                // as DUAS guardas ficavam abertas — esta e a de `isConnecting`, que só é marcada
                // depois que o POST volta. Como `connectionTry` ainda era 0, `connectionDelay`
                // continuava 0 e o timer seguinte disparava na hora: cada `stompTopic()` de destino
                // novo nessa janela chamava `connect()` outra vez e gerava OUTRO POST. Um load de
                // `/desktop` assina ~10 destinos distintos. O worker descarta os sockets extras,
                // mas os POSTs já tinham ido — e, antes da correção da API, cada um rotacionava a
                // sessão à toa.
                //
                // O `finalize` zera a guarda depois que o `next` já marcou `isConnecting` (o
                // `take(1)` completa em seguida), então a de `isConnecting` assume sem brecha. Zerar
                // no `finalize`, e não só no `next`, evita ficar preso para sempre se o `url()`
                // falhar ou completar vazio.
                this.url().pipe(take(1), finalize(() => {
                    this.connectionTimeout = null;
                })).subscribe((url) => {
                    this.initWorker();
                    console.debug(`get url done, ${url}`);
                    this.isConnecting = true;
                    this.connectionTry++;
                    this.stompSubject.next({
                        type: StompEventType.OPEN,
                        connect: {
                            url: url,
                            heartbeat: this.heartbeatInterval
                        }
                    });
                    // R2CLOUD-1165: a partir daqui a tentativa depende inteiramente do worker
                    // responder. Se ele ficar mudo, nada mais no fluxo zera `isConnecting`.
                    this.workerWatchdog.arm(() => this.onWorkerSilent());
                });
            }, applyReconnectJitter(this.connectionDelay));
        }
        console.debug(`stomp connect, isConnected: ${this.isConnected}, isConnecting: ${this.isConnecting}, delay: ${this.connectionDelay}`);

    }

    protected disconnect(): void {
        console.debug(`stomp disconnect, isConnected: ${this.isConnected}, isConnecting: ${this.isConnecting}`);
        // R2CLOUD-1165: pedido explícito de desconexão encerra a tentativa em curso. Sem isto, o
        // watchdog armado por um `connect()` anterior reconectaria depois do logout — e aí as
        // assinaturas ainda vivas dos componentes fariam a guarda de `stompSubscriptions` passar.
        this.workerWatchdog.disarm();
        if (this.isConnected || this.isConnecting) {
            this.stompSubject.next({
                type: StompEventType.CLOSE
            });
        }
    }

    private onClose(closeEvent: StompClose): void {
        console.debug(`stomp close, code: ${closeEvent.code}, reason: ${closeEvent.reason}, wasClean: ${closeEvent.wasClean}`);
        this.isConnected = false;
        this.isConnecting = false;
        console.debug('stomp CloseEvent:');
        console.debug(closeEvent);
        if (closeEvent.code === 1000) {
            switch (closeEvent.reason) {
                case 'Duplicate login':
                    this._connectionStatus$.next(StompConnectionStatus.DUPLICATE_LOGIN);
                    break;
                case 'Exceeded Maximum Users':
                    this._connectionStatus$.next(StompConnectionStatus.EXCEEDED_MAXIMUM_USERS);
                    break;
                default:
                    // não zerar o backoff aqui: o close 1000 genérico inclui timeout de heartbeat,
                    // shutdown e reload do ingress (R2CLOUD-1098) — zerar mantinha todos os browsers
                    // reconectando em lockstep a cada ~2s. O reset acontece no onStompConnect().
                    this._connectionStatus$.next(StompConnectionStatus.CONNECTION_ENDED);
                    this.connect();
            }
        } else {
            this._connectionStatus$.next(StompConnectionStatus.DISCONNECTED);
            this.connect();
        }
    }

    private onStompConnect(): void {
        this.isConnected = true;
        this.isConnecting = false;
        if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }
        this.connectionDelay = 0;
        this.connectionTry = 0;
        this._connectionStatus$.next(StompConnectionStatus.OK);
        for (const observer of this.stompSubscriptions) {
            this.sendSubscribe(observer);
        }
    }

    private onOpen(): void {
        this.stompSubject.next({
            type: StompEventType.FRAME,
            frame: {command: 'CONNECT', headers: {'accept-version': '1.2', 'heart-beat': `${this.heartbeatInterval},${this.heartbeatInterval}`}}
        });
    }

    private onError(error: string): void {
        console.debug('stomp onError:');
        console.error(error);
    }

    private onStompFrame(frame: StompFrame): void {
        switch (frame.command) {
            case 'CONNECTED':
                this._username$.next(frame.headers['user-name']);
                this.onStompConnect();
                break;
            case 'MESSAGE':
                console.debug('received message');
                console.debug(frame);
                const subscriptionId = frame.headers['subscription'];
                const stompSubscription = this.stompSubscriptions.find((observer) => observer.id === subscriptionId);
                if (stompSubscription) {
                    console.debug('emitting message to observer:');
                    console.debug(stompSubscription);
                    stompSubscription.emit(trimNull(frame.body ?? ''));
                }
                break;
            case 'ERROR':
                this.onError(JSON.stringify(frame));
                break;
            case 'DISCONNECT':
                this.disconnect();
                break;
        }
    }

    private sendSubscribe(stompSubscription: StompSubscription<any>): void {
        console.debug('sendSubscribe');
        console.debug(stompSubscription);
        if (this.isConnected) {
            this.stompSubject.next({
                type: StompEventType.FRAME,
                frame: {
                    command: 'SUBSCRIBE', headers: {
                        id: stompSubscription.id,
                        destination: stompSubscription.destination
                    }
                }
            });
        }
    }

    private sendUnsubscribe(stompSubscription: StompSubscription<any>): void {
        if (this.isConnected) {
            this.stompSubject.next({
                type: StompEventType.FRAME,
                frame: {command: 'UNSUBSCRIBE', headers: {id: stompSubscription.id}}
            });
        }
    }

    ngOnDestroy(): void {
        this.workerWatchdog.disarm();
        if (this.connectionTimeout) {
            // sem isso, um timer de reconexão pendente (janela de até ~90s) reconectaria um service destruído
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }
        this._connectionStatus$.next(StompConnectionStatus.DESTROYED);
        this._connectionStatus$.complete();
        for (const stompSubscription of this.stompSubscriptions) {
            stompSubscription.complete();
        }
        this.stompSubject.next({
            type: StompEventType.CLOSE
        });
    }

    private onStompEvent(stompEvent: StompEvent): void {
        // R2CLOUD-1165: QUALQUER mensagem prova que o worker está vivo — inclusive o `OPEN`, que ele
        // devolve assim que o socket abre. Desarmar aqui, e não só em `onStompConnect()`/`onClose()`,
        // é o que impede o watchdog de derrubar um socket JÁ ABERTO cujo frame `CONNECTED` demorou —
        // que é exatamente o caminho que produziria duas conexões.
        this.workerWatchdog.disarm();
        switch (stompEvent.type) {
            case StompEventType.OPEN:
                this.onOpen();
                break;
            case StompEventType.CLOSE:
                if (stompEvent.close) {
                    this.onClose(stompEvent.close);
                }
                break;
            case StompEventType.FRAME:
                if (stompEvent.frame) {
                    this.onStompFrame(stompEvent.frame);
                }
                break;
            case StompEventType.ERROR:
                if (stompEvent.error) {
                    this.onError(stompEvent.error);
                }
                break;
        }
    }
}
